/**
 * PATH: packages/email-hub/src/feature/components/InboxScopeBar.tsx
 * FILE_ID: EMAIL-INBOX-SCOPE-BAR-001
 * PURPOSE: One-click scope switch for the mail working set:
 *          Focus (clean inbox) / Everything (inbox incl. done/swept) /
 *          Done (archived) / Junk (binned).
 * WHY: Post-sweep, Done/Junk mail lives one navigation away but the
 *      folders are buried in the sidebar — operators second-guessed
 *      "where did my mail go" (2026-07-07 filter-UX ask). The scope bar
 *      makes the four working sets one visible click apart.
 * LANDMINE: Must sit on its OWN full-width row (see InboxListToolbar).
 *      When it shared a row with Inbox/AI Analysis tabs, "Everything"
 *      crushed to "E..." in the narrow list pane (operator 2026-07-16).
 * LANDMINE: Nothing here may use equal-width pills or clipping. Equal
 *      widths make the LONGEST label clip first while short ones keep
 *      slack, and B-03's count glyphs then push even "Focus" to "F.."
 *      (operator 2026-07-25). Pills are content-sized; width pressure is
 *      absorbed by dropping counts, then shortening labels — never by
 *      clipping.
 * LANDMINE: The measured element is the OUTER full-width wrapper, never
 *      the pill bar itself. The bar is content-sized, so measuring it
 *      reported ~250px regardless of the pane, which pinned the tier at
 *      `standard` in a wide pane and made the roomy tier unreachable.
 *      A content-sized element cannot tell you how much room it has.
 */
import { useLayoutEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { cn } from '../../_internal/lib/utils'
import { emailPath } from '../lib/basePath'

export type InboxScope = 'focus' | 'all' | 'done' | 'junk'

/** Below this measured width, "Everything" shortens to "All" (title keeps
 *  the full meaning).
 *
 *  Raised from 300 to 420 on 2026-07-25: 300 was chosen when the pills held
 *  labels ONLY. B-03 later added a count glyph to every pill without moving
 *  the threshold, so the 300-419px band tried to fit four full labels plus
 *  four counts and clipped instead. */
export const SCOPE_BAR_COMPACT_PX = 420

/** Below this measured width only the ACTIVE scope keeps its count glyph.
 *  The numbers stay in every tab's accessible name, so nothing is lost to
 *  assistive tech. Sighted operators keep the one number that answers the
 *  question a narrow pane actually raises — "how much is in the set I am
 *  looking at" — instead of losing all four, which is what dropping them
 *  wholesale used to do at exactly the width the reader pane creates. */
export const SCOPE_BAR_COUNTS_MIN_PX = 300

/** How the bar degrades as its container narrows. */
export type ScopeBarTier = 'roomy' | 'standard' | 'tight'

/** Pure I→O: measured border-box width → presentation tier.
 *
 *  Width `0` means "not measured yet" (jsdom, first paint before the
 *  ResizeObserver fires) and resolves to `roomy` so the bar never flashes a
 *  degraded state it does not need. */
export function resolveScopeBarTier(width: number): ScopeBarTier {
  if (width <= 0) return 'roomy'
  if (width < SCOPE_BAR_COUNTS_MIN_PX) return 'tight'
  if (width < SCOPE_BAR_COMPACT_PX) return 'standard'
  return 'roomy'
}

/** Pure I→O: tier + whether this pill is the active one → show its count.
 *
 *  Split out so the degradation rule is testable without a DOM and without
 *  a ResizeObserver. */
export function shouldShowScopeCount(
  tier: ScopeBarTier,
  isActive: boolean,
): boolean {
  return tier !== 'tight' || isActive
}

export const SCOPE_ITEMS: Array<{
  key: InboxScope
  label: string
  shortLabel: string
  title: string
}> = [
  {
    key: 'focus',
    label: 'Focus',
    shortLabel: 'Focus',
    title: 'Your working inbox — swept/done mail hidden',
  },
  {
    key: 'all',
    label: 'Everything',
    shortLabel: 'All',
    title: 'Inbox including done/swept mail (junk stays out)',
  },
  {
    key: 'done',
    label: 'Done',
    shortLabel: 'Done',
    title: 'Archived / done mail — nothing is deleted',
  },
  {
    key: 'junk',
    label: 'Junk',
    shortLabel: 'Junk',
    title: 'Binned / junk mail — recoverable any time',
  },
]

/** Route target per scope. Focus/Everything share the inbox route —
 *  Everything adds ?scope=all which the page maps to include_archived. */
export function scopeHref(scope: InboxScope): string {
  switch (scope) {
    case 'all':
      return `${emailPath('/inbox')}?scope=all`
    case 'done':
      return emailPath('/archived')
    case 'junk':
      return emailPath('/trash')
    default:
      return emailPath('/inbox')
  }
}

function formatCount(n: number): string {
  if (n >= 1000) return `${Math.floor(n / 1000)}k+`
  return String(n)
}

export default function InboxScopeBar({
  active,
  counts,
}: {
  active: InboxScope
  /** Folder totals from EmailStats.by_folder (B-03). Optional until loaded. */
  counts?: Partial<Record<InboxScope, number>>
}) {
  const navigate = useNavigate()
  const rootRef = useRef<HTMLDivElement>(null)
  const [tier, setTier] = useState<ScopeBarTier>('roomy')

  useLayoutEffect(() => {
    const el = rootRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const apply = (width: number) => setTier(resolveScopeBarTier(width))
    apply(el.clientWidth)
    const observer = new ResizeObserver((entries) => {
      apply(entries[0]?.contentRect.width ?? el.clientWidth)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return (
    /* The wrapper is what gets measured: it spans the row, so its width is
       the room available. The bar inside stays content-sized. */
    <div ref={rootRef} className="w-full min-w-0">
      <div
        role="tablist"
        aria-label="Mail scope"
        data-testid="inbox-scope-bar"
        data-tier={tier}
        /* w-fit, not w-full — a full-width bar made each pill a QUARTER of
           a wide list pane ("stretched, ugly", operator 2026-07-17). The
           bar hugs its four labels like a normal segmented control. */
        className="flex w-fit max-w-full items-center gap-0.5 rounded-lg bg-muted/60 p-0.5"
      >
        {SCOPE_ITEMS.map(({ key, label, shortLabel, title }) => {
          const shown = tier === 'roomy' ? label : shortLabel
          const count = counts?.[key]
          const showCount =
            typeof count === 'number'
            && shouldShowScopeCount(tier, active === key)
          return (
            <button
              key={key}
              role="tab"
              type="button"
              aria-selected={active === key}
              aria-label={
                typeof count === 'number' ? `${label}, ${count}` : label
              }
              title={title}
              onClick={() => {
                if (active !== key) navigate(scopeHref(key))
              }}
              className={cn(
                'px-2.5 py-1 text-xs font-semibold rounded-md transition-colors',
                'whitespace-nowrap text-center inline-flex items-center justify-center gap-1',
                active === key
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <span>{shown}</span>
              {showCount ? (
                <span
                  className="tabular-nums text-[10px] font-medium opacity-70"
                  data-testid={`inbox-scope-count-${key}`}
                  aria-hidden="true"
                >
                  {formatCount(count as number)}
                </span>
              ) : null}
            </button>
          )
        })}
      </div>
    </div>
  )
}
